import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import { Container, Row, Col, Card, Form, InputGroup, Pagination, FormSelect } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { FileIcon, defaultStyles } from 'react-file-icon';
import { IconDownload, IconSearch, IconX } from '@tabler/icons-react';
import useDownloader from 'react-use-downloader';
import Markdown from 'react-markdown';
import { toast } from 'react-toastify';
import RejectOrder from './Modal/RejectOrder';
import Loader from '../../Loader';

const Orders = () => {
    const { download } = useDownloader();
    const [loader, setLoader] = useState(false);
    const [orders, setOrders] = useState([]);
    const [orderId, setOrderId] = useState(null);
    const [modalOrderReject, setModelOrderReject] = useState(false);
    const [searchKey, setSearchKey] = useState({ key: "" });
    const timerRef = useRef(null);
    const [pagination, setPagination] = useState({
        limit: 10,
        skip: 0,
        pages: [],
        nextPage: false,
        currentPage: 1,
        previousPage: false
    });
    const token = useSelector((state) => state.token);

    const getOrders = (currentPage = 1, search = searchKey.key) => {
        setLoader(true);
        axios({
            method: "GET",
            url: `${process.env.REACT_APP_BASE_URL}/order?page=${currentPage}&limit=${pagination.limit}&search=${search}`,
            headers: {
                "Authorization": `Bearer ${token}`
            },
        }).then(res => {
            setOrders(res.data.orders);
            setLoader(false);
            setPagination((preState) => ({
                ...preState,
                pages: res.data.pages,
                nextPage: res.data.nextPage,
                currentPage: res.data.currentPage,
                previousPage: res.data.previousPage
            }));
        }).catch(err => {
            setLoader(false);
        });
    };

    const handleStatusChange = (status, note, orderId) => {
        setLoader(true);
        axios({
            method: "PUT",
            url: `${process.env.REACT_APP_BASE_URL}/order/status/${orderId}`,
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            data: JSON.stringify({
                status: status,
                note: note
            })
        }).then(res => {
            getOrders(pagination.currentPage);
            setLoader(false);
            toast(res.data.message, {
                type: "success",
                position: "top-right",
                type: "success"
            });
        }).catch(err => {
            setLoader(false);
            if (err?.response?.data?.errors) {
                toast(err.response.data.errors[0].msg, {
                    theme: "light",
                    position: "top-right",
                    type: "error"
                });
            } else {
                toast(err?.response?.data?.message || err.message || "Failed to update status", {
                    theme: "light",
                    position: "top-right",
                    type: "error"
                });
            }
        });
    }

    const handleStatus = (status, note, orderId, paymentPath = "") => {
        if (status == "Completed") {
            return (
                <>
                    <p className='text-success'>{status}</p>
                    <button className='button yellow p-1 rounded-pill' onClick={() => download(`${process.env.REACT_APP_IMG_BASE_URL}${paymentPath}`, paymentPath.split('pdf/')[1])}>
                        <IconDownload size={20} stroke={2} />
                    </button>
                </>
            )
        } else if (status == "Rejected") {
            return (
                <>
                    <p className='text-danger'>{status}</p>
                    <p>{note}</p>
                </>
            )
        } else if (status == "Processing") {
            return (
                <FormSelect size='sm' onChange={(t) => {
                    setOrderId(orderId);
                    if (t.target.value === "Processing") {
                        return;
                    } else if (t.target.value === "Rejected") {
                        return setModelOrderReject(true);
                    }
                    handleStatusChange(t.target.value, null, orderId);
                }}>
                    <option value="Processing" selected>Processing</option>
                    <option value="Completed">Completed</option>
                    <option value="Rejected">Rejected</option>
                </FormSelect>);
        } else {
            return (
                <FormSelect size='sm' onChange={(t) => {
                    setOrderId(orderId);
                    if (t.target.value === "Pending") {
                        return;
                    } else if (t.target.value === "Rejected") {
                        return setModelOrderReject(true);
                    }
                    handleStatusChange(t.target.value, null, orderId);
                }}>
                    <option value="Pending">Pending</option>
                    <option value="Processing">Processing</option>
                    <option value="Completed">Completed</option>
                    <option value="Rejected">Rejected</option>
                </FormSelect>);
        }
    }

    const debounced = (value) => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
        timerRef.current = setTimeout(() => {
            getOrders(1, value);
        }, 500);
    }

    useEffect(() => {
        getOrders();
    }, []);

    return (
        <>
            <section className="admin_main full">
                <Container fluid>
                    <Row>
                        <Col md={12}>
                            <Card>
                                <Card.Header as="h5">
                                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                                        <div className="head_title">
                                            <h3 className="mb-0">Orders</h3>
                                        </div>
                                        <InputGroup className="w-auto search-box">
                                            <Form.Control
                                                placeholder="Search"
                                                type='search'
                                                value={searchKey.key}
                                                onChange={(t) => {
                                                    setSearchKey((prevState) => ({
                                                        ...prevState,
                                                        key: t.target.value
                                                    }));
                                                    debounced(t.target.value);
                                                }} />
                                            {searchKey.key?.length > 0 ?
                                                <InputGroup.Text onClick={() => {
                                                    setSearchKey((prevState) => ({ ...prevState, key: "" }));
                                                    debounced("")
                                                }} type="button" className='button'>
                                                    <IconX size={24} stroke={2} />
                                                </InputGroup.Text>
                                                :
                                                <InputGroup.Text
                                                    id="basic-addon1"
                                                    type="button"
                                                    onClick={() => {
                                                        if (searchKey.key?.length <= 0) {
                                                            return
                                                        }
                                                        getOrders(1, searchKey.key);
                                                    }}
                                                    className='button'>
                                                    <IconSearch size={24} stroke={2} />
                                                </InputGroup.Text>
                                            }
                                        </InputGroup>
                                    </div>
                                </Card.Header>
                                <Card.Body>
                                    {orders?.length > 0 ?
                                        <div className="table-responsive">
                                            <table className="client_table order-tb">
                                                <thead>
                                                    <tr className="d-none d-lg-table-row">
                                                        <th width="6%"><small>Date</small></th>
                                                        <th width="6%"><small>Service type</small></th>
                                                        <th width="6%"><small>Recipient amount</small></th>
                                                        <th width="6%"><small>Currency</small></th>
                                                        <th width="6%"><small>Recipient currency</small></th>
                                                        <th width="6%"><small>Recipient name</small></th>
                                                        <th width="16%"><small>Recipient bank details</small></th>
                                                        <th width="6%"><small>Payment type</small></th>
                                                        <th width="6%"><small>Rate</small></th>
                                                        <th width="6%"><small>Sub total</small></th>
                                                        <th width="6%"><small>Additional fee</small></th>
                                                        <th width="6%"><small>Estimate total</small></th>
                                                        <th width="6%"><small>Proof of payment</small></th>
                                                        <th width="6%" style={{ minWidth: "120px" }}><small>Invoice</small></th>
                                                        <th width="6%" style={{ minWidth: "120px" }}><small>Status</small></th>
                                                        <th width="6%"><small>Email</small></th>
                                                        <th width="6%"><small>Order ID</small></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {orders.map((o, index) => {
                                                        return (<tr key={index} className="tr">
                                                            <td>
                                                                <strong className="ht">Date : </strong>
                                                                <small>{`${new Date(o.createdAt).toLocaleString()}`}</small>
                                                            </td>
                                                            <td>
                                                                <strong className="ht">Service type : </strong>
                                                                <small>{o.quotation.serviceType}</small>
                                                            </td>
                                                            <td>
                                                                <strong className='ht'>Recipient amount :</strong>
                                                                <small>{parseFloat(o?.quotation?.recipientAmount) > 0.01 ? parseFloat(o?.quotation?.recipientAmount).toLocaleString(undefined, { maximumFractionDigits:2, minimumFractionDigits:2 }) : parseFloat(o?.quotation?.recipientAmount)}</small>
                                                            </td>
                                                            <td>
                                                                <strong className="ht">Currency : </strong>
                                                                <small>{o?.quotation?.currency ? o.quotation.currency.toUpperCase() : "not found"}</small>
                                                            </td>
                                                            <td>
                                                                <strong className="ht">Recipient currency : </strong>
                                                                <small>{o?.quotation.recipientCurrency ? o.quotation.recipientCurrency.toUpperCase() : "not found"}</small>
                                                            </td>
                                                            <td>
                                                                <strong className="ht">Recipient name : </strong>
                                                                <small>{o?.receiverName ? o.receiverName : "not found"}</small>
                                                            </td>
                                                            <td>
                                                                <strong className="ht">Recipient bank details : </strong>
                                                                <small style={{ whiteSpace: "pre-line" }}><Markdown>{o.receiverBankDetails}</Markdown></small>
                                                            </td>
                                                            <td>
                                                                <strong className="ht">Payment type : </strong>
                                                                <small>{o.paymentType ? o.paymentType == "Company" ? `${o.paymentType}(${o.companyName})` : o.paymentType : "-"}</small>
                                                            </td>
                                                            <td>
                                                                <strong className="ht">Rate : </strong>
                                                                <small>{o?.quotation?.recipientCurrency === "ngn" ? o?.quotation?.displayRate ? parseFloat(o.quotation.displayRate) > 0.01 ? parseFloat(o.quotation.displayRate).toLocaleString(undefined, { maximumFractionDigits:2, minimumFractionDigits:2 }) : parseFloat(o.quotation.displayRate) : "-" : o?.quotation?.rate ? parseFloat(o.quotation.rate) > 0.01 ? parseFloat(o.quotation.rate).toLocaleString(undefined, { maximumFractionDigits:2, minimumFractionDigits:2 }) : parseFloat(o.quotation.rate) : "-"}</small>
                                                            </td>
                                                            <td>
                                                                <strong className="ht">Sub total : </strong>
                                                                <small>{o?.quotation?.subTotal ? parseFloat(o.quotation.subTotal) > 0.01 ? parseFloat(o.quotation.subTotal).toLocaleString(undefined, { maximumFractionDigits:2, minimumFractionDigits:2 }) : parseFloat(o.quotation.subTotal) : "-"}</small>
                                                            </td>
                                                            <td>
                                                                <strong className="ht">Additional fee : </strong>
                                                                <small>{o?.quotation?.additionalFee ? parseFloat(o.quotation.additionalFee) > 0.01 ? parseFloat(o.quotation.additionalFee).toLocaleString(undefined, { maximumFractionDigits:2, minimumFractionDigits:2 }) : parseFloat(o.quotation.additionalFee) : "-"}</small>
                                                            </td>
                                                            <td>
                                                                <strong className="ht">Estimate total : </strong>
                                                                <small>{o?.quotation?.estimateTotal ? parseFloat(o.quotation.estimateTotal) > 0.01 ? parseFloat(o.quotation.estimateTotal).toLocaleString(undefined, { maximumFractionDigits:2, minimumFractionDigits:2 }) : parseFloat(o.quotation.estimateTotal) : "-"}</small>
                                                            </td>
                                                            <td>
                                                                <strong className="ht">Proof Of payment : </strong>
                                                                <small className='d-flex align-items-center gap-2'>
                                                                    <div className="doc-file">
                                                                        <FileIcon extension={o?.proofOfPayment?.split('.')[1]} {...defaultStyles[o?.proofOfPayment?.split('.')[1]]} />
                                                                    </div>

                                                                    <button className='button yellow p-1 rounded-pill' onClick={() => download(`${process.env.REACT_APP_IMG_BASE_URL}${o?.proofOfPayment}`, o?.proofOfPayment?.split('payment/')[1])}>
                                                                        <IconDownload size={20} stroke={2} />
                                                                    </button>
                                                                </small>
                                                            </td>
                                                            <td>
                                                                <strong className="ht">Invoice : </strong>
                                                                {o?.invoice ?
                                                                    <small className='d-flex align-items-center gap-2'>
                                                                        <div className="doc-file">
                                                                            <FileIcon extension={o?.invoice?.split('.')[1]} {...defaultStyles[o?.invoice?.split('.')[1]]} />
                                                                        </div>

                                                                        <button className='button yellow p-1 rounded-pill' onClick={() => {
                                                                            download(`${process.env.REACT_APP_IMG_BASE_URL}${o?.invoice}`, o?.invoice?.split('invoice/')[1]);
                                                                        }}>
                                                                            <IconDownload size={20} stroke={2} />
                                                                        </button>
                                                                    </small>
                                                                    : <small>-</small>}
                                                            </td>
                                                            <td>
                                                                <strong className="ht">Status : </strong>
                                                                <small>{handleStatus(o.status, o?.rejectionNote, o._id, o.paymentInvoice)}</small>
                                                            </td>
                                                            <td>
                                                                <strong className="ht">Email : </strong>
                                                                <small>{o?.user?.email ? o.user.email : "not found"}</small>
                                                            </td>
                                                            <td>
                                                                <strong className="ht">Order ID : </strong>
                                                                <small>{o.orderId}</small>
                                                            </td>
                                                        </tr>);
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                        : <p>Orders is not found</p>}
                                    <div className="d-flex justify-content-center mt-4">
                                        {pagination.previousPage || pagination.nextPage ?
                                            <Pagination>
                                                {pagination.previousPage ?
                                                    <Pagination.Item
                                                        onClick={() => { getOrders(pagination.currentPage - 1) }}
                                                        key={pagination.currentPage - 1}>
                                                        {pagination.currentPage - 1}
                                                    </Pagination.Item>
                                                    : null}
                                                <Pagination.Item
                                                    active>
                                                    {pagination.currentPage}
                                                </Pagination.Item>
                                                {pagination.nextPage ?
                                                    <Pagination.Item
                                                        onClick={() => { getOrders(pagination.currentPage + 1) }}
                                                        key={pagination.currentPage + 1}>
                                                        {pagination.currentPage + 1}
                                                    </Pagination.Item>
                                                    : null}
                                            </Pagination>
                                            : null}
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </section>
            <Loader show={loader} />
            <RejectOrder
                show={modalOrderReject}
                orderId={orderId}
                getData={() => { getOrders() }}
                onHide={() => setModelOrderReject(false)} />
        </>
    )
}

export default Orders